import { supabase } from './supabase';
import { supabaseData } from './supabaseData';

/**
 * Check if company has an active messaging subscription
 *
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} { hasSubscription, subscription, domainVerified, credits }
 */
export async function checkMessagingSubscription(companyId) {
  try {
    const response = await fetch(`/api/messaging?action=check-subscription&companyId=${companyId}`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to check subscription');
    }

    return result;
  } catch (error) {
    return {
      hasSubscription: false,
      subscription: null,
      domainVerified: false,
      error: error.message
    };
  }
}

/**
 * Get messaging stats for a user
 *
 * @param {string} userId - User UUID
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} Stats object
 */
export async function getMessagingStats(userId, companyId) {
  try {
    const response = await fetch(`/api/messaging?action=get-stats&userId=${userId}&companyId=${companyId}`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to get stats');
    }

    return result;
  } catch (error) {
    return { error: error.message };
  }
}

/**
 * Get company-wide messaging stats (for admins)
 *
 * @param {string} companyId - Company UUID
 * @returns {Promise<Object>} Company stats object
 */
export async function getCompanyMessagingStats(companyId) {
  try {
    const response = await fetch(`/api/messaging?action=get-company-stats&companyId=${companyId}`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to get company stats');
    }

    return result;
  } catch (error) {
    return { error: error.message };
  }
}

/**
 * Start company messaging subscription checkout
 *
 * @param {string} companyId - Company UUID
 * @param {string} planType - 'weekly' or 'monthly'
 * @returns {Promise<Object>} { success, url, userCount } - Redirect to url for checkout
 */
export async function startMessagingSubscription(companyId, planType) {
  try {
    const baseUrl = window.location.origin;
    const response = await fetch('/api/messaging', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'create-subscription',
        companyId,
        planType,
        successUrl: `${baseUrl}/settings?messaging=success`,
        cancelUrl: `${baseUrl}/settings?messaging=canceled`
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to start subscription');
    }

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Purchase additional message blocks for company
 *
 * @param {string} companyId - Company UUID
 * @param {number} quantity - Number of 1,000-message blocks
 * @returns {Promise<Object>} { success, url } - Redirect to url for checkout
 */
export async function purchaseMessageBlocks(companyId, quantity = 1) {
  try {
    const baseUrl = window.location.origin;
    const response = await fetch('/api/messaging', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'purchase-block',
        companyId,
        quantity,
        successUrl: `${baseUrl}/settings?blocks=success`,
        cancelUrl: `${baseUrl}/settings?blocks=canceled`
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to purchase blocks');
    }

    return result;
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Generate mailto link for email fallback
 *
 * @param {Object} params
 * @param {string} params.to - Recipient email
 * @param {string} params.subject - Email subject
 * @param {string} params.body - Email body (plain text)
 * @param {string} params.cc - CC recipients (optional)
 * @param {string} params.bcc - BCC recipients (optional)
 * @returns {string} mailto: URL
 */
export function generateMailtoLink({ to, subject, body, cc, bcc }) {
  const params = new URLSearchParams();

  if (subject) params.append('subject', subject);
  if (body) params.append('body', body);
  if (cc) params.append('cc', cc);
  if (bcc) params.append('bcc', bcc);

  const queryString = params.toString();
  return `mailto:${encodeURIComponent(to)}${queryString ? '?' + queryString : ''}`;
}

/**
 * Open mailto link in user's default email client
 *
 * @param {Object} params - Same as generateMailtoLink
 */
export function openMailto(params) {
  const link = generateMailtoLink(params);
  window.location.href = link;
}

/**
 * Send SMS via Twilio
 * Requires active messaging subscription
 *
 * @param {Object} params
 * @param {string} params.phone - Customer phone number
 * @param {string} params.message - Message text (with variables already replaced)
 * @param {string} params.customerId - Customer UUID
 * @param {string} params.companyId - Company UUID
 * @param {string} params.userId - User UUID sending the message
 * @param {string} params.jobId - Job UUID (optional)
 * @param {string} params.templateId - Template UUID (optional)
 * @param {string} params.sentBy - User UUID who sent the message
 * @returns {Promise<Object>} { success, messageId, error }
 */
export async function sendSMS({ phone, message, customerId, companyId, userId, jobId = null, templateId = null, sentBy = null }) {
  try {
    // Call our Twilio API endpoint with user/company for credit checking
    const response = await fetch('/api/sms-send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        to: phone,
        message: message,
        userId,
        companyId,
        customerId
      })
    });

    const result = await response.json();

    if (!response.ok) {
      // Check for subscription/credit errors
      if (result.code === 'NO_SUBSCRIPTION') {
        return {
          success: false,
          error: result.message,
          code: 'NO_SUBSCRIPTION',
          needsSubscription: true
        };
      }
      if (result.code === 'INSUFFICIENT_CREDITS') {
        return {
          success: false,
          error: result.message,
          code: 'INSUFFICIENT_CREDITS',
          needsCredits: true,
          creditsNeeded: result.creditsNeeded,
          creditsAvailable: result.creditsAvailable
        };
      }
      throw new Error(result.error || 'Failed to send SMS');
    }

    // Log to database
    const { error } = await supabaseData
      .from('communications')
      .insert([{
        company_id: companyId,
        customer_id: customerId,
        job_id: jobId,
        type: 'sms',
        direction: 'outbound',
        message: message,
        status: 'sent',
        sent_at: new Date().toISOString(),
        provider_response: {
          provider: 'twilio',
          messageId: result.messageId,
          status: result.status,
          phone: phone,
          timestamp: new Date().toISOString()
        },
        sent_by: sentBy,
        sent_by_user_id: userId
      }]);

    if (error) {
    }

    return {
      success: true,
      messageId: result.messageId,
      status: result.status
    };

  } catch (error) {
    // Log failed attempt to database
    try {
      await supabaseData
        .from('communications')
        .insert([{
          company_id: companyId,
          customer_id: customerId,
          job_id: jobId,
          type: 'sms',
          direction: 'outbound',
          message: message,
          status: 'failed',
          sent_at: new Date().toISOString(),
          provider_response: {
            provider: 'twilio',
            error: error.message,
            phone: phone,
            timestamp: new Date().toISOString()
          },
          sent_by: sentBy,
          sent_by_user_id: userId
        }]);
    } catch (logError) {
    }

    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Replace template variables with actual values
 *
 * @param {string} template - Template text with {variables}
 * @param {Object} values - Object with variable values
 * @returns {string} Message with variables replaced
 */
export function replaceTemplateVariables(template, values) {
  let message = template;

  Object.keys(values).forEach(key => {
    const placeholder = `{${key}}`;
    message = message.replace(new RegExp(placeholder, 'g'), values[key] || '');
  });

  return message;
}

/**
 * Format phone number for display
 *
 * @param {string} phone - Raw phone number
 * @returns {string} Formatted phone (555) 123-4567
 */
export function formatPhone(phone) {
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
}

/**
 * Validate phone number
 *
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid US phone number
 */
export function isValidPhone(phone) {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || cleaned.length === 11;
}

/**
 * Send Email via Resend
 * Requires active messaging subscription with verified domain
 *
 * @param {Object} params
 * @param {string} params.to - Recipient email address
 * @param {string} params.subject - Email subject
 * @param {string} params.html - HTML body (optional if text provided)
 * @param {string} params.text - Plain text body (optional if html provided)
 * @param {string} params.customerId - Customer UUID
 * @param {string} params.companyId - Company UUID
 * @param {string} params.userId - User UUID sending the email
 * @param {string} params.companyName - Company name for "via Work Van" format
 * @param {string} params.jobId - Job UUID (optional)
 * @param {string} params.replyTo - Reply-to email (optional)
 * @param {string} params.sentBy - User UUID who sent the message
 * @returns {Promise<Object>} { success, messageId, error }
 */
export async function sendEmail({
  to,
  subject,
  html,
  text,
  customerId,
  companyId,
  userId,
  companyName,
  jobId = null,
  replyTo = null,
  sentBy = null
}) {
  try {
    // Call our Resend API endpoint with user/company for credit checking
    const response = await fetch('/api/email-send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        to,
        subject,
        html,
        text,
        replyTo,
        userId,
        companyId,
        customerId,
        companyName
      })
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      // Check for subscription/credit errors
      if (result.code === 'NO_SUBSCRIPTION') {
        return {
          success: false,
          error: result.message,
          code: 'NO_SUBSCRIPTION',
          needsSubscription: true
        };
      }
      if (result.code === 'INSUFFICIENT_CREDITS') {
        return {
          success: false,
          error: result.message,
          code: 'INSUFFICIENT_CREDITS',
          needsCredits: true,
          creditsNeeded: result.creditsNeeded,
          creditsAvailable: result.creditsAvailable
        };
      }
      throw new Error(result.error || 'Failed to send email');
    }

    // Log to database
    const { error } = await supabaseData
      .from('communications')
      .insert([{
        company_id: companyId,
        customer_id: customerId,
        job_id: jobId,
        type: 'email',
        direction: 'outbound',
        message: text || html,
        status: 'sent',
        sent_at: new Date().toISOString(),
        provider_response: {
          provider: 'resend',
          messageId: result.messageId,
          status: result.status,
          email: to,
          subject: subject,
          timestamp: new Date().toISOString()
        },
        sent_by: sentBy,
        sent_by_user_id: userId
      }]);

    if (error) {
    }

    return {
      success: true,
      messageId: result.messageId,
      status: result.status
    };

  } catch (error) {
    // Log failed attempt to database
    try {
      await supabaseData
        .from('communications')
        .insert([{
          company_id: companyId,
          customer_id: customerId,
          job_id: jobId,
          type: 'email',
          direction: 'outbound',
          message: text || html,
          status: 'failed',
          sent_at: new Date().toISOString(),
          provider_response: {
            provider: 'resend',
            error: error.message,
            email: to,
            subject: subject,
            timestamp: new Date().toISOString()
          },
          sent_by: sentBy,
          sent_by_user_id: userId
        }]);
    } catch (logError) {
    }

    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Validate email address
 *
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid email format
 */
export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Convert plain text to simple HTML
 * Preserves line breaks
 *
 * @param {string} text - Plain text content
 * @returns {string} HTML content
 */
export function textToHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');
}
